/**
 * Phase 17 Plan 17-02 — EditKpiTemplateDialog
 *
 * Admin-only dialog to update an existing KPI Template.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useUpdateKpiTemplateMutation } from '@/hooks/useKpiTemplateQueries';
import { toast } from '@/lib/toast';
import { KPI_UNITS, type KpiUnit } from '@/types/kpi';
import type { KpiTemplate, UpdateKpiTemplateBody } from '@/types/kpi-template';

const editTemplateSchema = z
  .object({
    name: z.string().min(3, 'Name must be at least 3 characters').max(100, 'Name must be at most 100 characters'),
    unit: z.enum([
      'Nos', 'Meters', 'RunningMeters', 'Hours', 'Kilometers',
      'Liters', 'Kilograms', 'Units', 'Trips', 'Custom',
    ] as const),
    customUnitText: z.string().max(50).optional(),
    description: z.string().max(500).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.unit === 'Custom') {
      if (data.customUnitText === undefined || data.customUnitText.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['customUnitText'],
          message: 'Custom unit text is required when unit is Custom',
        });
      }
    }
  });

type EditTemplateFormValues = z.infer<typeof editTemplateSchema>;

export interface EditKpiTemplateDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (v: boolean) => void;
  readonly template: KpiTemplate;
}

export function EditKpiTemplateDialog({
  open,
  onOpenChange,
  template,
}: EditKpiTemplateDialogProps): JSX.Element {
  const mutation = useUpdateKpiTemplateMutation();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<EditTemplateFormValues>({
    resolver: zodResolver(editTemplateSchema),
    defaultValues: {
      name: template.name,
      unit: template.unit,
      customUnitText: template.customUnitText ?? '',
      description: template.description ?? '',
    },
  });

  // Re-populate when template changes (e.g. different row clicked)
  useEffect(() => {
    reset({
      name: template.name,
      unit: template.unit,
      customUnitText: template.customUnitText ?? '',
      description: template.description ?? '',
    });
  }, [template, reset]);

  const watchedUnit = watch('unit');

  function handleClose(): void {
    setServerError(null);
    mutation.reset();
    onOpenChange(false);
  }

  function onSubmit(values: EditTemplateFormValues): void {
    setServerError(null);
    const body: UpdateKpiTemplateBody = {
      name: values.name,
      unit: values.unit,
      ...(values.unit === 'Custom' && values.customUnitText
        ? { customUnitText: values.customUnitText }
        : {}),
      ...(values.description && values.description.trim() !== ''
        ? { description: values.description }
        : {}),
    };
    mutation.mutate(
      { id: template.id, body },
      {
        onSuccess: () => {
          toast.success('KPI template updated');
          handleClose();
        },
        onError: (err) => {
          const msg = err.detail ?? err.message;
          setServerError(msg);
          toast.error('Failed to update template', { description: msg });
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent data-testid="edit-kpi-template-dialog">
        <DialogHeader>
          <DialogTitle>Edit KPI Template</DialogTitle>
          <DialogDescription>Update the template details.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="edit-template-name">Name</Label>
            <Input
              id="edit-template-name"
              data-testid="edit-template-name-input"
              {...register('name')}
            />
            {errors.name && (
              <p className="text-sm text-destructive" data-testid="edit-template-name-error">
                {errors.name.message}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="edit-template-unit">Unit</Label>
            <Select
              value={watchedUnit}
              onValueChange={(v) => { setValue('unit', v as KpiUnit); }}
            >
              <SelectTrigger id="edit-template-unit" data-testid="edit-template-unit-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {KPI_UNITS.map((u) => (
                  <SelectItem key={u} value={u}>
                    {u === 'RunningMeters' ? 'Running Meters' : u}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {watchedUnit === 'Custom' && (
            <div className="space-y-1">
              <Label htmlFor="edit-template-custom-unit">Custom Unit Text</Label>
              <Input
                id="edit-template-custom-unit"
                data-testid="edit-template-custom-unit-input"
                {...register('customUnitText')}
              />
              {errors.customUnitText && (
                <p className="text-sm text-destructive">
                  {errors.customUnitText.message}
                </p>
              )}
            </div>
          )}

          <div className="space-y-1">
            <Label htmlFor="edit-template-description">Description (optional)</Label>
            <Textarea
              id="edit-template-description"
              data-testid="edit-template-description-input"
              {...register('description')}
              rows={2}
            />
          </div>

          {serverError !== null && (
            <div
              className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="edit-template-error"
            >
              {serverError}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={mutation.isPending}
              data-testid="edit-template-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending}
              data-testid="edit-template-submit"
            >
              {mutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
